import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http, API_BASE } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import GuideListenButton from "@/components/GuideListenButton";
import RelatedReading from "@/components/RelatedReading";
import TranslatedProse from "@/components/TranslatedProse";
import ShareButton from "@/components/ShareButton";
import { ArrowLeft } from "@phosphor-icons/react";

/** Public reader for CMS articles/guides — /read/:pageType/:slug
 * (news, articles, etc. seeded by the editorial desk). */
export default function CmsArticlePage() {
  const { pageType, slug } = useParams();
  const [page, setPage] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPage(null); setMissing(false);
    http.get(`/cms/pages/by-slug/${pageType}/${slug}`)
      .then(({ data }) => setPage(data.page || data))
      .catch(async () => {
        // On-demand concept stories: generate once, then read like any article.
        if (slug.startsWith("science-concept-")) {
          try {
            await http.get(`/science/concepts/${slug.replace("science-concept-", "")}/article`);
            const { data } = await http.get(`/cms/pages/by-slug/${pageType}/${slug}`);
            setPage(data.page || data);
            return;
          } catch { /* fall through to missing */ }
        }
        setMissing(true);
      });
  }, [pageType, slug]);

  const bodyText = page ? (page.body_html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "";
  const fullText = page ? `${page.title}. ${page.subtitle || ""} ${bodyText}`.trim() : "";
  const docketTag = (page?.tags || []).find((t) => t.endsWith("-pulse") && t !== "nature-pulse") || page?.category;

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="cms-article-page">
      <div className="max-w-[760px] mx-auto px-5 md:px-8">
        <Link to="/articles" data-testid="cms-article-back"
          className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-muted,#3A3830)] hover:text-[#1B4332]">
          <ArrowLeft size={12} weight="bold" /> All articles
        </Link>

        {missing ? (
          <div className="mt-10" data-testid="cms-article-missing">
            <h1 className="font-serif-display text-3xl">Article not found.</h1>
            <p className="mt-2 text-sm text-[#3A3830]">It may have been unpublished — browse the <Link to="/articles" className="underline underline-offset-2">articles index</Link>.</p>
          </div>
        ) : !page ? (
          <div className="mt-10 font-mono-tech text-[10px] tracking-[0.3em] uppercase" data-testid="cms-article-loading">Loading…</div>
        ) : (
          <article className="mt-6">
            <div className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]" data-testid="cms-article-meta">
              {page.page_type} {page.category && <>· {page.category}</>} {page.published_at && <>· {page.published_at.slice(0, 10)}</>}
            </div>
            {page.author_name && (
              <div className="mt-1.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase text-[#6B7280]" data-testid="cms-article-author">
                By {page.author_name}
              </div>
            )}
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl leading-[1.05] tracking-tight" data-testid="cms-article-title">{page.title}</h1>
            {page.subtitle && <p className="mt-3 text-lg text-[#3A3830] font-serif-display italic">{page.subtitle}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <ListenTranslateBar text={fullText} testidPrefix="cms-article-tools" />
              <GuideListenButton testid="cms-article-listen" color="#B85F2A"
                sections={bodyText ? [{ title: page.title, text: fullText }] : []} />
              <ShareButton title={page.title} text={page.excerpt || page.subtitle || ""} shortKind="article" shortId={page.slug}
                variant="chip" size="md" testid="cms-article-share" />
            </div>

            {page.cover_image && (
              <figure className="mt-5 rounded-2xl overflow-hidden border border-black/10" data-testid="cms-article-cover">
                <img src={page.cover_image.startsWith("http") ? page.cover_image : `${API_BASE}${page.cover_image}`}
                  alt={page.title} className="w-full object-cover max-h-[380px]" loading="lazy" />
              </figure>
            )}

            <TranslatedProse cacheKey={`cms-${pageType}-${slug}`}>
              <div
                className="cms-article-body mt-6 text-[16px] leading-[1.75] text-[#22301F]"
                data-testid="cms-article-body"
                dangerouslySetInnerHTML={{ __html: page.body_html || "" }}
              />
            </TranslatedProse>

            {(page.tags || []).length > 0 && (
              <div className="mt-8 flex flex-wrap gap-1.5">
                {page.tags.map((t) => (
                  <span key={t} className="rounded-full border border-black/10 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.18em] uppercase">{t}</span>
                ))}
              </div>
            )}

            {docketTag && (
              <RelatedReading tag={docketTag} exclude={page.slug} limit={4}
                title="More from this desk" testid="cms-article-related" />
            )}
          </article>
        )}
      </div>
    </main>
  );
}
